import KmxChevronDownStroked from '@kmx/mui-icons/KmxChevronDownStroked';
import KmxChevronUpStroked from '@kmx/mui-icons/KmxChevronUpStroked';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';

import { emptyContentfulLinkFarmResponse, getContentfulLinkFarm } from '../../../api/content-service-api';
import { IContentfulLinkFarmResponse, ILinkFarmTab } from '../../../api/types/content-api';
import { KmxContainer } from '../../../components/KmxContainer';
import { ANALYTICS_CONSTANTS, trackEDDL } from '../../../utilities/analytics';
import { useMuiMediaQuery } from '../../../utilities/hooks';

const DEFAULT_HEIGHT = '192px';

const LinkFarm = () => {
    const [activeTab, setActiveTab] = useState(0);
    const [data, setTabs] = useState<IContentfulLinkFarmResponse>(emptyContentfulLinkFarmResponse);
    const [height, setHeight] = useState(DEFAULT_HEIGHT);
    const [isExpanded, setIsExpanded] = useState(false);

    const { isMobile } = useMuiMediaQuery();

    useEffect(() => {
        getContentfulLinkFarm().then((data) => {
            setTabs(data);
        });
    }, []);

    if (!data || data.tabItems.length === 0) {
        return <></>;
    }

    return (
        <KmxContainer component="section" id="link-farm" maxWidth="xl" sx={{ pt: { xs: 8, md: 12 } }}>
            <Stack component="section" spacing={{ xs: 2, md: 4 }}>
                <Typography component="h2" sx={{ typography: { xs: 'h5', sm: 'h4' } }}>
                    Find cars your way
                </Typography>
                <Box>
                    <Tabs
                        value={activeTab}
                        onChange={(_, selectedTab) => {
                            setIsExpanded(false);
                            setHeight(DEFAULT_HEIGHT);
                            setActiveTab(selectedTab);
                        }}
                        variant="scrollable"
                        scrollButtons="auto"
                        sx={{
                            '.MuiTabs-flexContainer': {
                                borderBottom: '1px solid #ccd0d6',
                            },
                        }}
                    >
                        {data.tabItems.map(({ tabResponsiveName, tabName }: ILinkFarmTab, i: number) => (
                            <Tab
                                key={tabName}
                                label={isMobile ? tabResponsiveName : tabName}
                                value={i}
                                onClick={() => {
                                    trackEDDL({
                                        event: ANALYTICS_CONSTANTS.EVENTS.CLICK.CTA,
                                        linkDetails: {
                                            name: tabName,
                                            position: 'Browse By',
                                        },
                                    });
                                    setActiveTab(i);
                                }}
                                data-kmx-analytics={`prop5=${tabName}`}
                            />
                        ))}
                    </Tabs>
                </Box>
                <Box
                    sx={{
                        height,
                        overflowY: 'hidden',
                        transition: 'height 0.5s ease',
                        willChange: 'height',
                    }}
                >
                    {data.tabItems.map(({ tabLinks }: ILinkFarmTab, i: number) => {
                        return (
                            <Box
                                key={tabLinks[i].linkText}
                                hidden={activeTab !== i}
                                role="tabpanel"
                                sx={{
                                    display: activeTab === i ? 'block' : 'none',
                                    columnCount: { xs: 2, sm: 3, md: 4, lg: 5 },
                                }}
                            >
                                {tabLinks.map(({ linkText, linkUrl }) => (
                                    <Box
                                        key={linkText}
                                        sx={{
                                            mb: 2,
                                            maxWidth: '170px',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                        }}
                                    >
                                        <Link
                                            noWrap
                                            variant="body1"
                                            color="textPrimary"
                                            href={linkUrl}
                                            rel="noopener noreferrer"
                                            target="_blank"
                                        >
                                            {linkText}
                                        </Link>
                                    </Box>
                                ))}
                            </Box>
                        );
                    })}
                </Box>

                {data.tabItems[activeTab].tabLinks.length > (isMobile ? 10 : 20) && (
                    <Button
                        variant="outlined"
                        color="secondary"
                        sx={{ border: '1px solid #ccd0d6' }}
                        endIcon={isExpanded ? <KmxChevronUpStroked /> : <KmxChevronDownStroked />}
                        onClick={() => {
                            // Scroll to the top of the link farm section
                            document.getElementById('link-farm')?.scrollIntoView({ behavior: 'smooth' });

                            // Toggle height
                            if (isExpanded) {
                                setTimeout(() => setHeight(DEFAULT_HEIGHT), 100);
                                setIsExpanded(false);
                            } else {
                                const tabpanels =
                                    document.querySelectorAll<HTMLElement>('#link-farm [role="tabpanel"]');
                                const activeTabpanel = tabpanels[activeTab];
                                if (activeTabpanel) {
                                    setIsExpanded(true);
                                    setHeight(`${activeTabpanel.offsetHeight.toString()}px`);
                                }
                            }
                        }}
                        aria-hidden="true"
                    >
                        {isExpanded ? 'View less' : 'View more'}
                    </Button>
                )}
            </Stack>
        </KmxContainer>
    );
};

export default LinkFarm;
